/**
 * Customise drawer: customisation step builds a copy-to-clipboard message (no contact POST).
 * Newsletter still submits via a hidden iframe for Shopify spam protection.
 */
class CustomizeDrawerForms extends HTMLElement {
  connectedCallback() {
    this.#formWrap = this.querySelector('[data-customize-step="form"]');
    this.#successWrap = this.querySelector('[data-customize-step="success"]');
    this.#contactForm = this.querySelector('.pdp-drawer-customize__contact-form');
    this.#newsletterForm = this.querySelector('.pdp-drawer-customize__newsletter-form');
    this.#newsletterFields = this.querySelector('[data-customize-newsletter-fields]');
    this.#newsletterSuccess = this.querySelector('[data-customize-newsletter-success]');
    this.#contactIframeHost = this.querySelector('[data-customize-iframe-host]');
    this.#contactChallengeNote = this.querySelector('[data-customize-challenge-note]');
    this.#nlIframeHost = this.querySelector('[data-customize-newsletter-iframe-host]');
    this.#nlChallengeNote = this.querySelector('[data-customize-newsletter-challenge-note]');

    this.#consentCheckbox = this.querySelector('[data-customize-consent-checkbox]');
    this.#summarySection = this.querySelector('[data-customize-summary-section]');
    this.#summaryPre = this.querySelector('[data-customize-summary-pre]');
    this.#copyMetricsBtn = this.querySelector('[data-customize-copy-metrics]');
    this.#copySuccess = this.querySelector('[data-customize-copy-success]');

    this.#consentCheckbox?.addEventListener('change', this.#onConsentChange);
    this.#copyMetricsBtn?.addEventListener('click', this.#onCopyMetrics);

    this.#contactForm?.addEventListener('input', this.#onFormFieldChange);
    this.#contactForm?.addEventListener('change', this.#onFormFieldChange);
    this.#contactForm?.addEventListener('submit', this.#onContactFormSubmit);

    this.#newsletterForm?.addEventListener('submit', this.#onNewsletterSubmit);

    this.#drawer = this.closest('pdp-side-drawer-component');
    this.#drawer?.addEventListener('dialog:close', this.#reset);

    this.#applyConsentSummaryState();
  }

  disconnectedCallback() {
    this.#drawer?.removeEventListener('dialog:close', this.#reset);
    this.#consentCheckbox?.removeEventListener('change', this.#onConsentChange);
    this.#copyMetricsBtn?.removeEventListener('click', this.#onCopyMetrics);
    this.#contactForm?.removeEventListener('input', this.#onFormFieldChange);
    this.#contactForm?.removeEventListener('change', this.#onFormFieldChange);
    this.#contactForm?.removeEventListener('submit', this.#onContactFormSubmit);
    this.#newsletterForm?.removeEventListener('submit', this.#onNewsletterSubmit);
    this.#detachNewsletterLoadListener();
  }

  /** @type {HTMLElement | null} */
  #formWrap = null;
  /** @type {HTMLElement | null} */
  #successWrap = null;
  /** @type {HTMLFormElement | null} */
  #contactForm = null;
  /** @type {HTMLFormElement | null} */
  #newsletterForm = null;
  /** @type {HTMLElement | null} */
  #newsletterFields = null;
  /** @type {HTMLElement | null} */
  #newsletterSuccess = null;
  /** @type {HTMLElement | null} */
  #contactIframeHost = null;
  /** @type {HTMLElement | null} */
  #contactChallengeNote = null;
  /** @type {HTMLElement | null} */
  #nlIframeHost = null;
  /** @type {HTMLElement | null} */
  #nlChallengeNote = null;
  /** @type {HTMLElement | null} */
  #drawer = null;
  /** @type {HTMLInputElement | null} */
  #consentCheckbox = null;
  /** @type {HTMLElement | null} */
  #summarySection = null;
  /** @type {HTMLPreElement | null} */
  #summaryPre = null;
  /** @type {HTMLButtonElement | null} */
  #copyMetricsBtn = null;
  /** @type {HTMLElement | null} */
  #copySuccess = null;
  /** @type {HTMLIFrameElement | null} */
  #newsletterIframe = null;
  #newsletterAwaiting = false;
  /** @type {(() => void) | null} */
  #newsletterLoadListener = null;

  #reset = () => {
    this.#detachNewsletterLoadListener();
    this.#newsletterAwaiting = false;

    if (this.#formWrap) this.#formWrap.hidden = false;
    if (this.#successWrap) this.#successWrap.hidden = true;

    this.#contactForm?.reset();
    this.#contactForm?.removeAttribute('target');
    const bodyField = this.#contactForm?.querySelector('textarea[name="contact[body]"]');
    if (bodyField) bodyField.value = '';

    this.#newsletterForm?.reset();
    this.#newsletterForm?.removeAttribute('target');
    if (this.#newsletterFields) this.#newsletterFields.hidden = false;
    if (this.#newsletterSuccess) this.#newsletterSuccess.hidden = true;

    this.#clearContactChallengeUi();
    this.#clearNewsletterChallengeUi();
    if (this.#contactIframeHost) this.#contactIframeHost.hidden = true;
    if (this.#nlIframeHost) this.#nlIframeHost.hidden = true;

    if (this.#consentCheckbox) this.#consentCheckbox.checked = false;
    this.#summarySection?.setAttribute('hidden', '');
    this.#copySuccess?.setAttribute('hidden', '');

    const nBtn = this.#newsletterForm?.querySelector('button[type="submit"]');
    if (nBtn) {
      nBtn.disabled = false;
      if (nBtn.dataset.originalLabel) nBtn.textContent = nBtn.dataset.originalLabel;
    }
  };

  #clearContactChallengeUi() {
    this.#contactChallengeNote?.setAttribute('hidden', '');
    this.#contactIframeHost?.classList.remove('is-challenge-active');
  }

  #clearNewsletterChallengeUi() {
    this.#nlChallengeNote?.setAttribute('hidden', '');
    this.#nlIframeHost?.classList.remove('is-challenge-active');
  }

  #detachNewsletterLoadListener() {
    if (this.#newsletterLoadListener && this.#newsletterIframe) {
      this.#newsletterIframe.removeEventListener('load', this.#newsletterLoadListener);
    }
    this.#newsletterLoadListener = null;
  }

  /**
   * @param {string} selectId
   */
  #selectDisplayText(selectId) {
    const el = this.#contactForm?.querySelector(`#${selectId}`);
    if (!(el instanceof HTMLSelectElement)) return '';
    const opt = el.options[el.selectedIndex];
    if (!opt || opt.value === '') return '';
    return opt.textContent?.trim() || opt.value;
  }

  #buildMetricsSummaryText() {
    const nameEl = this.#contactForm?.querySelector('[name="contact[name]"]');
    const nameVal = nameEl instanceof HTMLInputElement ? nameEl.value.trim() : '';

    const star = this.#selectDisplayText('customize-star-sign');

    const sillyEl = this.#contactForm?.querySelector('#customize-silly-score');
    let silly = '';
    if (sillyEl instanceof HTMLInputElement && sillyEl.value.trim()) {
      silly = `${sillyEl.value.trim()} % SILLY`;
    }

    const eye = this.#selectDisplayText('customize-eye');
    const hair = this.#selectDisplayText('customize-hair');
    const skin = this.#selectDisplayText('customize-skin');

    const u = (s) => (s ?? '').toUpperCase();
    const fill = (value, width = 38) => {
      const text = u(value);
      if (!text) return '_'.repeat(width);
      return text;
    };

    return [
      'DEAD SERIOUS JEWELLERY CO.',
      'CUSTOM COMMISSION REQUEST FORM',
      'FORM REF: DSJC-CCR-01',
      '',
      'PLEASE COMPLETE ALL FIELDS IN BLOCK CAPITALS USING BLACK OR BLUE INK.',
      'ILLEGIBLE SUBMISSIONS MAY RESULT IN PROCESSING DELAYS.',
      '',
      'SECTION 1 — APPLICANT PARTICULARS',
      '',
      `FULL LEGAL NAME: ${fill(nameVal)}`,
      `DATE OF BIRTH / ASTROLOGICAL DESIGNATION: ${fill(star, 12)}`,
      `SILLY SCORE (AS ASSESSED): ${fill(silly, 27)}`,
      '',
      'SECTION 2 — PHYSICAL CHARACTERISTICS FOR SPECIFICATION PURPOSES',
      '',
      `EYE COLOUR: ${fill(eye, 42)}`,
      `HAIR COLOUR: ${fill(hair, 41)}`,
      `SKIN TONE: ${fill(skin, 43)}`,
      '',
      'SECTION 3 — DECLARATION',
      '',
      'I CONFIRM THAT THE SILLY SCORE REFERENCED ABOVE IS, TO THE BEST OF',
      'MY KNOWLEDGE:',
      '',
      '[ ] ACCURATE',
      '[ ] INACCURATE',
      '[ ] FAIR, THOUGH SUBJECT TO INTERPRETATION',
      '',
      'BY CHECKING ANY BOX ABOVE, I HEREBY AGREE TO LEAD WITH LOVE AT ALL',
      'TIMES, IN PERPETUITY, WITHOUT EXCEPTION.',
      '',
      'SECTION 4 — ITEM SELECTION',
      '',
      'HAVING CONDUCTED A THOROUGH REVIEW OF THE CATALOGUE AVAILABLE AT',
      'WWW.TDSJWLR.COM, I WISH TO FORMALLY REGISTER INTEREST IN THE',
      'FOLLOWING ITEM(S), IN ORDER OF PREFERENCE:',
      '',
      '1. ___________________________________________________',
      '2. ___________________________________________________',
      '3. ___________________________________________________',
      '',
      'SECTION 5 — REQUEST FOR INFORMATION',
      '',
      'KINDLY ADVISE, AT YOUR EARLIEST ADMINISTRATIVE CONVENIENCE:',
      '',
      '(A) NEXT STEPS',
      '(B) ESTIMATED TIMELINE',
      '(C) APPLICABLE PRICE',
      '',
      'SECTION 6 — SIGN-OFF',
      '',
      `SUBMITTED BY: ${fill(nameVal, 41)}`,
      'DATE: __________________________________________________',
      '',
      'FOR OFFICE USE ONLY — DO NOT WRITE BELOW THIS LINE',
      'RECEIVED BY: _______ PROCESSED: [ ] Y [ ] N FILE REF: _______',
    ].join('\n');
  }

  #updateSummaryPre() {
    if (!this.#summaryPre) return;
    this.#summaryPre.textContent = this.#buildMetricsSummaryText();
  }

  #applyConsentSummaryState() {
    if (this.#consentCheckbox?.checked) {
      this.#summarySection?.removeAttribute('hidden');
      this.#updateSummaryPre();
    } else {
      this.#summarySection?.setAttribute('hidden', '');
      this.#copySuccess?.setAttribute('hidden', '');
    }
  }

  #onConsentChange = () => {
    this.#applyConsentSummaryState();
  };

  #onFormFieldChange = () => {
    if (this.#consentCheckbox?.checked && this.#summarySection && !this.#summarySection.hasAttribute('hidden')) {
      this.#updateSummaryPre();
    }
  };

  #onContactFormSubmit = (e) => {
    e.preventDefault();
  };

  #onCopyMetrics = async () => {
    const text = this.#buildMetricsSummaryText();
    try {
      await navigator.clipboard.writeText(text);
      this.#copySuccess?.removeAttribute('hidden');
    } catch {
      /* clipboard denied */
    }
  };

  /**
   * @param {Document | null | undefined} doc
   * @param {string} href
   */
  #isBotChallenge(doc, href) {
    const h = href.toLowerCase();
    if (h.includes('/challenge')) return true;
    const text = doc?.body?.innerText || '';
    return /verified before you continue|verify your connection|needs to be verified|hcaptcha|i.?m not a robot/i.test(
      text
    );
  }

  /**
   * @param {string} href
   * @param {string} token
   */
  #urlIndicatesSuccess(href, token) {
    try {
      const u = new URL(href, window.location.origin);
      if (u.searchParams.get(token) === 'ok') return true;
    } catch {
      /* ignore */
    }
    return href.includes(`${token}=ok`);
  }

  #ensureNewsletterIframe() {
    if (!this.#nlIframeHost) return null;
    if (!this.#newsletterIframe) {
      const f = document.createElement('iframe');
      f.name = 'customizeDrawerNewsletterTarget';
      f.title = 'Newsletter signup';
      f.setAttribute('tabindex', '-1');
      this.#nlIframeHost.appendChild(f);
      this.#newsletterIframe = f;
    }
    return this.#newsletterIframe;
  }

  #finishNewsletterSuccess() {
    this.#newsletterAwaiting = false;
    this.#detachNewsletterLoadListener();
    this.#clearNewsletterChallengeUi();
    if (this.#nlIframeHost) this.#nlIframeHost.hidden = true;

    const submitBtn = this.#newsletterForm?.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = false;
      if (submitBtn.dataset.originalLabel) submitBtn.textContent = submitBtn.dataset.originalLabel;
    }

    if (this.#newsletterFields) this.#newsletterFields.hidden = true;
    if (this.#newsletterSuccess) this.#newsletterSuccess.hidden = false;
  }

  #onNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = this.#newsletterForm;
    if (!form || !this.#newsletterFields || !this.#newsletterSuccess) return;

    if (!form.reportValidity()) return;

    const iframe = this.#ensureNewsletterIframe();
    if (!iframe || !this.#nlIframeHost) {
      alert('Something went wrong. Please try again.');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn && !submitBtn.dataset.originalLabel) {
      submitBtn.dataset.originalLabel = submitBtn.textContent?.trim() || 'SEND';
    }
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.loadingText || '…';
    }

    this.#clearNewsletterChallengeUi();
    this.#detachNewsletterLoadListener();
    this.#newsletterAwaiting = true;
    this.#nlIframeHost.hidden = false;
    this.#nlIframeHost.classList.add('is-challenge-active');

    form.target = iframe.name;

    this.#newsletterLoadListener = () => {
      if (!this.#newsletterAwaiting || !this.#newsletterIframe) return;
      let href = '';
      try {
        href = this.#newsletterIframe.contentWindow?.location?.href || '';
      } catch {
        return;
      }
      if (!href || href === 'about:blank') return;

      const doc = this.#newsletterIframe.contentWindow?.document;

      if (this.#urlIndicatesSuccess(href, 'customize_drawer_nl')) {
        this.#finishNewsletterSuccess();
        return;
      }

      if (doc && this.#isBotChallenge(doc, href)) {
        this.#nlChallengeNote?.removeAttribute('hidden');
        this.#nlIframeHost?.classList.add('is-challenge-active');
      }
    };

    this.#newsletterIframe.addEventListener('load', this.#newsletterLoadListener);
    form.submit();
  };
}

if (!customElements.get('customize-drawer-forms')) {
  customElements.define('customize-drawer-forms', CustomizeDrawerForms);
}

export { CustomizeDrawerForms };
